import React, {useEffect, useRef, useState} from 'react';
import {contacts} from '../data';

type Msg = { role: 'user' | 'assistant'; content: string };

const GREETING =
    "Hi — I'm Roman's AI assistant. Ask me about his experience, stack, or a specific project.";

const SUGGESTIONS = [
    'What is his AI experience?',
    'Why hire Roman?',
    'Tell me about Riskified',
];

const FALLBACK =
    `I'm offline right now — reach Roman directly at ${contacts.email} or grab the CV above.`;

const ENDPOINT = '/api/chat';
const MAX_INPUT = 300; // matches MAX_CHARS in functions/api/chat.ts — a CV question is short
const COOLDOWN_S = 15; // pause between messages to stay well under backend/Gemini limits

// Questions the typewriter placeholder cycles through (char-by-char) while the
// input is idle and unfocused.
const PROMPTS = [
    "what's his strongest stack?",
    'is he open to relocation?',
    'show me the Riskified work',
    'does he know Kubernetes?',
    'what has he shipped with AI?',
];

export function ChatTerminal() {
    const [messages, setMessages] = useState<Msg[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [focused, setFocused] = useState(false);
    const [ph, setPh] = useState('');
    const [caretLeft, setCaretLeft] = useState('0px');
    const [nativeCaret, setNativeCaret] = useState(false);
    const bodyRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const tw = useRef({pi: 0, ci: 0, dir: 1});
    const nativeCaretTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const syncCaret = () => {
        const el = inputRef.current;
        if (!el) return;
        const pos = el.selectionStart ?? el.value.length;
        setCaretLeft(`calc(${pos}ch - ${el.scrollLeft}px)`);
    };

    // While the input is focused, listen for selectionchange on the document.
    // When it fires we switch to the native caret so iOS gestures (space-bar
    // drag, magnifier) feel smooth. After 150 ms of silence we sync the block
    // caret position and switch back.
    useEffect(() => {
        if (!focused) return;
        const onSelectionChange = () => {
            setNativeCaret(true);
            if (nativeCaretTimer.current) clearTimeout(nativeCaretTimer.current);
            nativeCaretTimer.current = setTimeout(() => {
                syncCaret();
                setNativeCaret(false);
            }, 150);
        };
        document.addEventListener('selectionchange', onSelectionChange);
        return () => {
            document.removeEventListener('selectionchange', onSelectionChange);
            if (nativeCaretTimer.current) clearTimeout(nativeCaretTimer.current);
        };
    }, [focused]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const el = bodyRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, loading]);

    // tick the post-send cooldown down to zero
    useEffect(() => {
        if (cooldown <= 0) return;
        const t = setTimeout(() => setCooldown(c => Math.max(0, c - 1)), 1000);
        return () => clearTimeout(t);
    }, [cooldown]);

    // animated placeholder — type a prompt out, pause, delete, move to the next.
    // only runs while the placeholder is actually visible (idle + unfocused).
    const idle = !focused && input.length === 0;
    useEffect(() => {
        if (!idle) return;
        let timer: ReturnType<typeof setTimeout>;
        const tick = () => {
            const st = tw.current;
            const word = PROMPTS[st.pi];
            st.ci += st.dir;
            setPh(word.slice(0, st.ci));
            let delay: number;
            if (st.dir > 0 && st.ci === word.length) {
                delay = 1500;
                st.dir = -1;
            } else if (st.dir < 0 && st.ci === 0) {
                st.dir = 1;
                st.pi = (st.pi + 1) % PROMPTS.length;
                delay = 400;
            } else {
                delay = st.dir > 0 ? 55 + Math.random() * 55 : 32;
            }
            timer = setTimeout(tick, delay);
        };
        tick();
        return () => clearTimeout(timer);
    }, [idle]);

    const send = async (text: string) => {
        const trimmed = text.trim().slice(0, MAX_INPUT);
        if (!trimmed || loading || cooldown > 0) return;

        const history = [...messages, {role: 'user', content: trimmed} as Msg];
        setMessages(history);
        setInput('');
        setLoading(true);
        setCooldown(COOLDOWN_S); // start the pause as soon as the message is sent

        try {
            const res = await fetch(ENDPOINT, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                // send only the last turns to keep the context small
                body: JSON.stringify({messages: history.slice(-8)}),
            });
            // a 429 (rate limit) still carries a friendly `reply` — show it
            const data = await res.json().catch(() => null);
            const reply = (data?.reply ?? '').toString().trim();
            setMessages([...history, {role: 'assistant', content: reply || FALLBACK}]);
        } catch {
            setMessages([...history, {role: 'assistant', content: FALLBACK}]);
        } finally {
            setLoading(false);
        }
    };

    const onSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        void send(input);
    };

    // typing is always allowed; only sending is gated by loading + cooldown
    const canSend = !loading && cooldown === 0 && input.trim().length > 0;
    const nearLimit = input.length >= MAX_INPUT * 0.9;
    const fresh = messages.length === 0;

    return (
        <div className="chat-term" aria-label="Chat with Roman's AI assistant">
            <div className="chat-term__bar">
                <div className="chat-term__bar-left">
                    <span className="chat-term__dot" style={{background: 'oklch(0.74 0.18 50)'}}/>
                    <span className="chat-term__dot" style={{background: 'var(--accent)'}}/>
                    <span className="chat-term__dot" style={{background: 'oklch(0.52 0.20 300)'}}/>
                    <span className="chat-term__title">ask-roman — zsh</span>
                </div>
                <span className="chat-term__live"><i/>online</span>
            </div>

            <div className="chat-term__body" ref={bodyRef} role="log" aria-live="polite">
                <div className="chat-term__msg chat-term__msg--bot">
                    <span className="chat-term__who">roman.ai</span>
                    {GREETING}
                </div>

                {messages.map((m, i) => (
                    m.role === 'user' ? (
                        <div key={i} className="chat-term__msg chat-term__msg--user">{m.content}</div>
                    ) : (
                        <div key={i} className="chat-term__msg chat-term__msg--bot">
                            <span className="chat-term__who">roman.ai</span>
                            {m.content}
                        </div>
                    )
                ))}

                {loading && (
                    <div className="chat-term__typing" aria-label="Assistant is typing">
                        <span className="chat-term__who">roman.ai</span>
                        <div className="chat-term__typing-dots"><span/><span/><span/></div>
                    </div>
                )}

                {fresh && (
                    <div className="chat-term__chips">
                        {SUGGESTIONS.map(s => (
                            <button key={s} type="button" className="chat-chip" onClick={() => void send(s)}>
                                {s}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {fresh && (
                <div className="chat-term__hint">
                    <span className="chat-term__hint-arrow">↓</span>
                    <span>ask me anything — I actually answer in real time</span>
                </div>
            )}

            <form className="chat-term__form" onSubmit={onSubmit}>
                <span className="chat-term__prompt" aria-hidden="true">&gt;</span>
                <div className="chat-term__field" onClick={() => inputRef.current?.focus()}>
                    <input
                        ref={inputRef}
                        className="chat-term__input"
                        style={{caretColor: nativeCaret ? 'var(--accent)' : 'transparent'}}
                        value={input}
                        maxLength={MAX_INPUT}
                        onChange={e => { setInput(e.target.value); syncCaret(); }}
                        onSelect={syncCaret}
                        onScroll={syncCaret}
                        onKeyUp={syncCaret}
                        onMouseUp={syncCaret}
                        onTouchEnd={syncCaret}
                        onFocus={() => { setFocused(true); syncCaret(); }}
                        onBlur={() => { setFocused(false); setNativeCaret(false); }}
                        aria-label="Ask a question"
                        autoComplete="off"
                    />
                    {!idle && !nativeCaret && <span className="chat-term__caret" style={{position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: caretLeft}} aria-hidden="true"/>}
                    {idle && (
                        <div className="chat-term__ph" aria-hidden="true">
                            {cooldown > 0 ? `please wait ${cooldown}s…` : ph}
                            <span className="chat-term__caret"/>
                        </div>
                    )}
                </div>
                <span
                    className={`chat-term__meta${cooldown > 0 || nearLimit ? ' is-warn' : ''}`}
                    aria-live="polite"
                >
                    {`${input.length}/${MAX_INPUT}`}{cooldown > 0 ? ` · ${cooldown}s` : ''}
                </span>
                <button
                    className="chat-term__send"
                    type="submit"
                    disabled={!canSend}
                    aria-label={cooldown > 0 ? `Wait ${cooldown} seconds` : 'Send'}
                >
                    ↵
                </button>
            </form>
        </div>
    );
}
